import React, { useState, useEffect } from 'react';
import { 
  X, 
  Route, 
  ExternalLink, 
  Copy, 
  Check, 
  Trash2, 
  Calendar,
  Zap,
} from 'lucide-react';
import { Spot, UserLocation, Language } from '../types/spot';
import { generateMultiSpotRouteUrl, getSpotMapLinks } from '../services/mapLinks';
import { formatDistance, formatTransitBadge } from '../services/geolocation';
import { optimizeRouteWalkingOrder } from '../services/momentEngine';
import { getImmediateSpotImageUrl, getCategoryPlaceholder } from '../services/imageResolver';
import confetti from 'canvas-confetti';

interface RoutePlannerModalProps {
  savedSpots: Spot[];
  allSpots: Spot[];
  userLocation: UserLocation;
  language: Language;
  onClose: () => void;
  onRemoveSaved: (spotId: string) => void;
  onSelectSpot: (spot: Spot) => void;
}

export const RoutePlannerModal: React.FC<RoutePlannerModalProps> = ({
  savedSpots,
  allSpots,
  userLocation,
  language,
  onClose,
  onSelectSpot,
}) => {
  const [routeSpots, setRouteSpots] = useState<Spot[]>(() => [...savedSpots]);
  const [copiedList, setCopiedList] = useState(false);
  const [optimizedNotice, setOptimizedNotice] = useState<string | null>(null);

  // Keep in sync if savedSpots changes
  useEffect(() => {
    setRouteSpots([...savedSpots]);
  }, [savedSpots]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const toggleSpotInRoute = (spotId: string) => {
    if (routeSpots.find((s) => s.id === spotId)) {
      setRouteSpots(routeSpots.filter((s) => s.id !== spotId));
    } else {
      const spotToAdd = allSpots.find((s) => s.id === spotId);
      if (spotToAdd) setRouteSpots([...routeSpots, spotToAdd]);
    }
  };

  const t = {
    de: {
      title: 'Routen- & Tagesplaner',
      subtitle: 'Minimal-Walking Tour planen & direkt mit Naver & Kakao synchronisieren',
      optimizeBtn: 'Route optimieren (Kürzester Gehweg)',
      optimizedSaved: (km: string, pct: number) => `Gehweg um ${km} km minimiert (${pct}% weniger Laufen).`,
      alreadyOptimal: 'Deine Route ist bereits optimal geordnet (kürzester Gehweg).',
      openNaverRoute: 'Route in Naver Map öffnen',
      openKakaoRoute: 'Route in KakaoMap öffnen',
      copyListBtn: 'Koreanische Liste für Notizen/KakaoTalk kopieren',
      listCopied: 'Liste kopiert!',
      selectedStops: (n: number) => `Ausgewählte Stationen (${n})`,
      deselectAll: 'Alle abwählen',
      emptyTimeline: 'Wähle unten Spots aus, um deine Tagesroute zusammenzustellen.',
      quickAddTitle: 'Weitere Spots zur Route hinzufügen',
      removeTooltip: 'Entfernen',
      headerExport: (n: number) => `KOREA TAGESROUTE (${n} Spots - Gehweg-optimiert):\n\n`,
      footerExport: '\n\nErstellt mit K-SpotFinder',
    },
    en: {
      title: 'Route & Day Planner',
      subtitle: 'Plan minimal-walking tour & sync directly with Naver & Kakao Maps',
      optimizeBtn: 'Optimize Route (Shortest Walk)',
      optimizedSaved: (km: string, pct: number) => `Walking reduced by ${km} km (${pct}% less walking).`,
      alreadyOptimal: 'Your route is already optimal (shortest walking distance).',
      openNaverRoute: 'Open Route in Naver Map',
      openKakaoRoute: 'Open Route in KakaoMap',
      copyListBtn: 'Copy formatted list for Notes/KakaoTalk',
      listCopied: 'List copied!',
      selectedStops: (n: number) => `Selected Stops (${n})`,
      deselectAll: 'Deselect All',
      emptyTimeline: 'Select spots below to build your day route.',
      quickAddTitle: 'Add more spots to route',
      removeTooltip: 'Remove',
      headerExport: (n: number) => `KOREA DAY ROUTE (${n} spots - walking-optimized):\n\n`,
      footerExport: '\n\nCreated with K-SpotFinder',
    },
    kr: {
      title: '경로 및 일일 일정 플래너',
      subtitle: '도보 최소화 여행 경로 계획 및 네이버·카카오 지도 연동',
      optimizeBtn: '경로 최적화 (최단 도보 거리 정렬)',
      optimizedSaved: (km: string, pct: number) => `도보 이동 거리 ${km}km 단축 (${pct}% 감소).`,
      alreadyOptimal: '이미 최적의 순서로 정렬된 경로입니다.',
      openNaverRoute: '네이버 지도에서 경로 열기',
      openKakaoRoute: '카카오맵에서 경로 열기',
      copyListBtn: '노트/카카오톡용 일정 복사',
      listCopied: '목록 복사됨!',
      selectedStops: (n: number) => `선택된 장소 (${n})`,
      deselectAll: '전체 선택 해제',
      emptyTimeline: '아래에서 장소를 선택하여 일일 경로를 만들어보세요.',
      quickAddTitle: '경로에 장소 추가하기',
      removeTooltip: '삭제',
      headerExport: (n: number) => `한국 당일 여행 코스 (${n}개 장소):\n\n`,
      footerExport: '\n\nK-SpotFinder로 생성됨',
    },
  }[language];

  const handleOptimizeWalking = () => {
    if (routeSpots.length <= 1) return;

    const result = optimizeRouteWalkingOrder(routeSpots, userLocation);
    setRouteSpots(result.orderedSpots);
    if (result.savedDistanceKm > 0.1) {
      setOptimizedNotice(t.optimizedSaved(result.savedDistanceKm.toFixed(1), result.savedPercent));
      confetti({
        particleCount: 40,
        spread: 60,
        origin: { y: 0.7 },
        colors: ['#34d399', '#60a5fa', '#f59e0b'],
      });
    } else {
      setOptimizedNotice(t.alreadyOptimal);
    }

    setTimeout(() => setOptimizedNotice(null), 4000);
  };

  const handleOpenKakaoRoute = () => {
    if (routeSpots.length === 0) return;
    const url = generateMultiSpotRouteUrl(routeSpots, 'kakao');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenNaverRoute = () => {
    if (routeSpots.length === 0) return;
    const url = generateMultiSpotRouteUrl(routeSpots, 'naver');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyFormattedList = () => {
    const listText = routeSpots
      .map((s, i) => {
        const links = getSpotMapLinks(s, userLocation);
        return `${i + 1}. ${s.nameKr} (${language === 'de' ? s.nameDe : language === 'kr' ? s.nameKr : s.name})\n   🚇 ${s.subwayInfo}\n   🗺️ Naver: ${links.naverWeb}\n   🗺️ Kakao: ${links.kakaoWeb}`;
      })
      .join('\n\n');

    const fullText = `${t.headerExport(routeSpots.length)}${listText}${t.footerExport}`;
    navigator.clipboard.writeText(fullText);
    setCopiedList(true);
    setTimeout(() => setCopiedList(false), 2500);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-sheet"
        style={{ maxWidth: 680, display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header__left">
            <div className="modal-header__icon modal-header__icon--route">
              <Route style={{ width: 20, height: 20 }} />
            </div>
            <div>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
                {t.title}
              </h2>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {t.subtitle}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="modal-close">
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          
          {/* Walking Route Optimizer Banner */}
          {routeSpots.length >= 2 && (
            <button
              onClick={handleOptimizeWalking}
              className="btn btn--full"
              style={{
                background: 'var(--ink)',
                color: 'var(--ink-text)',
                fontWeight: 800,
                padding: '12px 16px',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                cursor: 'pointer',
                boxShadow: '0 0 16px rgba(0, 240, 255, 0.35)',
              }}
            >
              <Zap style={{ width: 16, height: 16, color: 'var(--ink-text)' }} />
              <span>{t.optimizeBtn}</span>
            </button>
          )}

          {optimizedNotice && (
            <div
              style={{
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.4)',
                color: '#34d399',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {optimizedNotice}
            </div>
          )}

          {/* Route Launchers */}
          <div className="route-grid">
            <button
              onClick={handleOpenNaverRoute}
              disabled={routeSpots.length === 0}
              className="btn btn--naver btn--lg btn--full"
            >
              <span>{t.openNaverRoute}</span>
              <ExternalLink style={{ width: 16, height: 16, opacity: 0.75 }} />
            </button>

            <button
              onClick={handleOpenKakaoRoute}
              disabled={routeSpots.length === 0}
              className="btn btn--kakao btn--lg btn--full"
            >
              <span>{t.openKakaoRoute}</span>
              <ExternalLink style={{ width: 16, height: 16, opacity: 0.75 }} />
            </button>
          </div>

          <button
            onClick={handleCopyFormattedList}
            className="btn btn--md btn--full"
            style={{ border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', justifyContent: 'center' }}
          >
            {copiedList ? <Check style={{ width: 16, height: 16, color: 'var(--success)' }} /> : <Copy style={{ width: 16, height: 16 }} />}
            <span>{copiedList ? t.listCopied : t.copyListBtn}</span>
          </button>

          {/* Timeline */}
          <div>
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <h3 className="flex items-center gap-2" style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <Calendar style={{ width: 16, height: 16, color: 'var(--warning)' }} />
                <span>{t.selectedStops(routeSpots.length)}</span>
              </h3>
              {routeSpots.length > 0 && (
                <button
                  onClick={() => setRouteSpots([])}
                  style={{ fontSize: 12, color: 'var(--error)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {t.deselectAll}
                </button>
              )}
            </div>

            {routeSpots.length === 0 ? (
              <div className="timeline-empty">
                {t.emptyTimeline}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {routeSpots.map((spot, index) => {
                  const title = language === 'de' ? spot.nameDe : language === 'kr' ? spot.nameKr : spot.name;
                  return (
                    <div key={spot.id} className="timeline-item">
                      <div className="timeline-item__number">{index + 1}</div>
                      <img
                        src={getImmediateSpotImageUrl(spot)}
                        alt={title}
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.currentTarget.src = getCategoryPlaceholder(spot.category); }}
                        className="timeline-item__thumb"
                        onClick={() => onSelectSpot(spot)}
                        style={{ cursor: 'pointer' }}
                      />
                      <div
                        className="timeline-item__info"
                        onClick={() => onSelectSpot(spot)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="timeline-item__kr">
                          <span className="truncate">{spot.nameKr}</span>
                          {spot.calculatedDistance !== undefined && (
                            <span className="timeline-item__distance" title={`Distanz: ${formatDistance(spot.calculatedDistance)}`}>
                              · {formatTransitBadge(spot.calculatedDistance, language)}
                            </span>
                          )}
                        </div>
                        <div className="timeline-item__title">{title}</div>
                      </div>
                      <button
                        onClick={() => toggleSpotInRoute(spot.id)}
                        style={{ padding: 6, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', transition: 'color 150ms' }}
                        title={t.removeTooltip}
                        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--error)')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
                      >
                        <Trash2 style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Add */}
          <div>
            <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              {t.quickAddTitle}
            </h3>
            <div className="add-spot-grid">
              {allSpots
                .filter((s) => !routeSpots.some((r) => r.id === s.id))
                .slice(0, 10)
                .map((spot) => {
                  const title = language === 'de' ? spot.nameDe : language === 'kr' ? spot.nameKr : spot.name;
                  return (
                    <button
                      key={spot.id}
                      onClick={() => toggleSpotInRoute(spot.id)}
                      className="add-spot-btn"
                    >
                      <img
                        src={getImmediateSpotImageUrl(spot)}
                        alt={title}
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.currentTarget.src = getCategoryPlaceholder(spot.category); }}
                        className="add-spot-btn__thumb"
                      />
                      <div className="add-spot-btn__info">
                        <div className="add-spot-btn__kr">{spot.nameKr}</div>
                        <div className="add-spot-btn__name">{title}</div>
                      </div>
                      <span className="add-spot-btn__plus">+</span>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
